import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, IconButton, CircularProgress,
  Grid, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, LinearProgress, Avatar,
  Divider, Alert, Button, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, TextField, MenuItem,
} from '@mui/material';
import {
  ArrowBack, Edit, Speed, Navigation,
  LocationOn, DirectionsCar, Phone, Delete, PhotoCamera,
  Timeline, BarChart, Map as MapIcon, Close, Refresh, UploadFile,
  Assessment, GpsFixed, GpsOff, Assignment, AssignmentReturn,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { InvokeApi, postImg, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import dayjs from 'dayjs';

// Leaflet loaded dynamically (same as reference Vehicles.js)
const MAP_LAYERS = {
  street:    { label: 'Street',    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',                                           attribution: '© OpenStreetMap contributors', maxZoom: 19 },
  satellite: { label: 'Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri', maxZoom: 19 },
  terrain:   { label: 'Terrain',   url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',                                             attribution: '© OpenTopoMap contributors', maxZoom: 17 },
  dark:      { label: 'Dark',      url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',                                attribution: '© CartoDB', maxZoom: 19 },
};

const InfoRow = ({ label, value }) => (
  <Box sx={{ py: 1, borderBottom: '1px solid #f5f5f5' }}>
    <Typography fontSize={11} color="text.secondary" fontWeight={500}>{label}</Typography>
    <Typography fontSize={13} fontWeight={600} color={value && value !== '—' ? '#1a1a1a' : 'text.disabled'}>{value || '—'}</Typography>
  </Box>
);

const VehicleDetail = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();

  // Core state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Vehicle');
  const [vehicle, setVehicle] = useState(null);
  const [gpsData, setGpsData] = useState(null);

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  // Edit form state
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docDialog, setDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ 
    title: '', 
    file: null, 
    document_type: 'other',
    notes: '',
    expiry_date: ''
  });
  const [docFileName, setDocFileName] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Track dialog — live GPS (Google Maps embed) + auto-refresh
  const [trackDialog, setTrackDialog] = useState(false);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackLocation, setTrackLocation] = useState(null);
  const trackIntervalRef = useRef(null);

  // Summary dialog
  const [summaryDialog, setSummaryDialog] = useState(false);
  const [summaryMonth, setSummaryMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Path dialog — daily GPS path on Leaflet map
  const [pathDialog, setPathDialog] = useState(false);
  const [pathDate, setPathDate] = useState(null);
  const [pathPoints, setPathPoints] = useState([]);
  const [pathLoading, setPathLoading] = useState(false);
  const [pathError, setPathError] = useState(null);
  const [pathMapLayer, setPathMapLayer] = useState('street');
  const pathMapRef    = useRef(null);
  const leafletMapRef = useRef(null);
  const tileLayerRef  = useRef(null);

  // Remote control state
  const [controlDialog, setControlDialog] = useState(false);
  const [controlCommand, setControlCommand] = useState('');
  const [controlReason, setControlReason] = useState('');
  const [controlLoading, setControlLoading] = useState(false);

  useEffect(() => {
    loadVehicleData();
  }, [vehicleId]);

  const loadVehicleData = async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      // Fetch vehicle details - use getVehicle (singular) for single vehicle detail
      const vRes = await InvokeApi(Config.base_url + apiList.getVehicle, { id: vehicleId });
      if (vRes?.code === '200' && vRes.vehicle) {
        const v = vRes.vehicle;
        setVehicle(v);
        // Initialize form with vehicle data (including new fields)
        setForm({
          id: v.id,
          vehicle_number: v.vehicle_number || '',
          vehicle_name: v.vehicle_name || '',
          chassis_number: v.chassis_number || '',
          model: v.model || '',
          color: v.color || '',
          year: v.year || '',
          status: v.status || 'active',
          device_imei: v.device_imei || '',
          battery_pct: v.battery_pct || '',
          battery_capacity: v.battery_capacity || '',
          battery_health_pct: v.battery_health_pct || '',
          range_km: v.range_km || '',
          odometer: v.odometer || '',
          charging_port_type: v.charging_port_type || '',
          max_charging_rate_kw: v.max_charging_rate_kw || '',
          insurance_expiry: v.insurance_expiry || '',
          rc_expiry: v.rc_expiry || '',
          notes: v.notes || '',
          vehicle_image: v.vehicle_image || '',
        });
      }

      // Fetch GPS data
      const gRes = await InvokeApi(Config.base_url + '/device/latest-location', { vehicle_id: vehicleId });
      if (gRes?.code === '200') setGpsData(gRes.location);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load documents when Documents tab is active
  useEffect(() => {
    if (activeTab !== 'Documents') return;
    const loadDocs = async () => {
      setDocsLoading(true);
      const data = await InvokeApi(Config.base_url + '/admin/vehicle/documents', { vehicle_id: vehicleId });
      setDocuments(data?.documents || []);
      setDocsLoading(false);
    };
    loadDocs();
  }, [activeTab, vehicleId]);

  // Load history when History tab is active
  useEffect(() => {
    if (activeTab !== 'History') return;
    const loadHistory = async () => {
      setHistoryLoading(true);
      const data = await InvokeApi(Config.base_url + apiList.getVehicleHistory, { id: vehicleId });
      setHistory(data?.history || []);
      setHistoryLoading(false);
    };
    loadHistory();
  }, [activeTab, vehicleId]);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('id', vehicleId);
      
      const res = await postImg(Config.base_url + apiList.uploadVehicleImage, formData);
      if (res?.code === '200') {
        setSnack({ open: true, msg: 'Image uploaded successfully', sev: 'success' });
        // Refresh vehicle data to get updated image
        await loadVehicleData();
      } else {
        setSnack({ open: true, msg: res?.message || 'Upload failed', sev: 'error' });
      }
    } catch (err) {
      setSnack({ open: true, msg: 'Upload error: ' + err.message, sev: 'error' });
    } finally {
      setUploadingImage(false);
    }
  };

  // ── Track: fetch live GPS location (auto-refreshes every 10 s) ─────────────
  const fetchLiveLocation = async (id) => {
    setTrackLoading(true);
    const res = await InvokeApi(Config.base_url + '/device/latest-location', { vehicle_id: id || vehicleId });
    if (res?.code === '200' && res.location) setTrackLocation(res.location);
    else if (res?.code !== '200') setTrackLocation(null);
    setTrackLoading(false);
  };

  const openTrackDialog = () => {
    setTrackDialog(true);
    setTrackLocation(null);
    fetchLiveLocation(vehicleId);
    trackIntervalRef.current = setInterval(() => fetchLiveLocation(vehicleId), 10000);
  };

  const closeTrackDialog = () => {
    if (trackIntervalRef.current) { clearInterval(trackIntervalRef.current); trackIntervalRef.current = null; }
    setTrackDialog(false);
    setTrackLocation(null);
  };

  // ── Summary ──────────────────────────────────────────────────────────────────
  const openSummaryDialog = async () => {
    setSummaryDialog(true);
    setSummaryData(null);
    setSummaryError(null);
    await fetchSummary(summaryMonth);
  };

  const fetchSummary = async (month) => {
    setSummaryLoading(true);
    setSummaryData(null);
    setSummaryError(null);
    try {
      const [yr, mo] = month.split('-');
      const res = await InvokeApi(Config.base_url + apiList.getVehicleSummary, {
        vehicle_id: vehicleId,
        month: parseInt(mo),
        year:  parseInt(yr),
      });
      if (res?.code === '200' && res?.summary) {
        setSummaryData(res.summary);
      } else {
        setSummaryError(res?.message || `No data (code ${res?.code})`);
      }
    } catch (err) {
      setSummaryError('Network error: ' + err.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  // ── Path dialog — Leaflet map (same as reference) ────────────────────────────
  const openPathDialog = async (day) => {
    setPathDate(day.date);
    setPathPoints([]);
    setPathError(null);
    setPathDialog(true);
    setPathLoading(true);
    try {
      const res = await InvokeApi(Config.base_url + apiList.getVehiclePathHistory, {
        vehicle_id: vehicleId,
        date: day.date,
      });
      if (res?.code === '200' && Array.isArray(res.points) && res.points.length > 0) {
        setPathPoints(res.points);
      } else {
        setPathError(res?.message || 'No GPS path data available for this date.');
      }
    } catch (err) {
      setPathError('Failed to load path: ' + err.message);
    } finally {
      setPathLoading(false);
    }
  };

  const closePathDialog = () => {
    if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null; tileLayerRef.current = null; }
    setPathDialog(false);
    setPathPoints([]);
    setPathError(null);
    setPathDate(null);
    setPathMapLayer('street');
  };

  // ── Leaflet: init map when points arrive ──────────────────────────────────────
  useEffect(() => {
    if (!pathDialog || pathLoading || pathPoints.length === 0 || !pathMapRef.current) return;
    if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null; tileLayerRef.current = null; }

    const initMap = () => {
      const L = window.L;
      if (!L) return;
      const coords = pathPoints.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]);
      const map = L.map(pathMapRef.current).fitBounds(coords);
      leafletMapRef.current = map;
      const layer = MAP_LAYERS[pathMapLayer] || MAP_LAYERS.street;
      tileLayerRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: layer.maxZoom }).addTo(map);
      L.polyline(coords, { color: '#1a237e', weight: 4, opacity: 0.85 }).addTo(map);
      // Start marker
      L.marker(coords[0], { icon: L.divIcon({ html: '<div style="background:#2e7d32;width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>', iconSize:[14,14], iconAnchor:[7,7], className:'' }) })
        .addTo(map).bindPopup(`<b>Start</b><br>${dayjs(String(pathPoints[0].timestamp).replace(' ','T')).format('hh:mm:ss A')}`).openPopup();
      // End marker
      const last = pathPoints[pathPoints.length - 1];
      L.marker(coords[coords.length - 1], { icon: L.divIcon({ html: '<div style="background:#c62828;width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>', iconSize:[14,14], iconAnchor:[7,7], className:'' }) })
        .addTo(map).bindPopup(`<b>End</b><br>${dayjs(String(last.timestamp).replace(' ','T')).format('hh:mm:ss A')}`);
    };

    if (!window.L) {
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link'); link.id='leaflet-css'; link.rel='stylesheet';
        link.href='https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link);
      }
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script'); script.id='leaflet-js';
        script.src='https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; script.onload=initMap; document.head.appendChild(script);
      }
    } else { initMap(); }

    return () => { if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null; tileLayerRef.current = null; } };
  }, [pathDialog, pathLoading, pathPoints]);

  // ── Swap tile layer without re-init ──────────────────────────────────────────
  useEffect(() => {
    const L = window.L;
    if (!L || !leafletMapRef.current || !tileLayerRef.current) return;
    const layer = MAP_LAYERS[pathMapLayer] || MAP_LAYERS.street;
    leafletMapRef.current.removeLayer(tileLayerRef.current);
    tileLayerRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: layer.maxZoom }).addTo(leafletMapRef.current);
  }, [pathMapLayer]);

  // cleanup track interval on unmount
  useEffect(() => () => { if (trackIntervalRef.current) clearInterval(trackIntervalRef.current); }, []);

  // Handle Remote Control
  const handleRemoteControl = async () => {
    if (!controlCommand) {
      setSnack({ open: true, msg: 'Please select a command', sev: 'error' });
      return;
    }

    if (controlCommand === 'immobilize' && !window.confirm('⚠️ WARNING: This will stop the vehicle remotely. The engine will be disabled. Are you sure?')) {
      return;
    }

    setControlLoading(true);
    try {
      const res = await InvokeApi(Config.base_url + '/admin/vehicle/remote-control', {
        vehicle_id: vehicleId,
        command: controlCommand,
        reason: controlReason.trim() || undefined,
      });

      if (res?.code === '200') {
        setSnack({ open: true, msg: res.message || 'Command sent successfully', sev: 'success' });
        setControlDialog(false);
        setControlCommand('');
        setControlReason('');
      } else {
        setSnack({ open: true, msg: res?.message || 'Failed to send command', sev: 'error' });
      }
    } catch (err) {
      setSnack({ open: true, msg: 'Error: ' + err.message, sev: 'error' });
    } finally {
      setControlLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this vehicle?')) return;
    const data = await InvokeApi(Config.base_url + apiList.deleteVehicle, { id: vehicleId });
    if (data?.code === '200') navigate('/vehicles');
    else alert(data?.message || 'Failed to delete');
  };

  // Edit form handlers
  const handleSave = async () => {
    if (!form.vehicle_number?.trim()) {
      setSnack({ open: true, msg: 'Vehicle number is required', sev: 'error' });
      return;
    }
    if (!form.chassis_number?.trim()) {
      setSnack({ open: true, msg: 'Chassis number is required', sev: 'error' });
      return;
    }
    if (!form.model?.trim()) {
      setSnack({ open: true, msg: 'Model is required', sev: 'error' });
      return;
    }

    setSaving(true);
    const data = await InvokeApi(Config.base_url + apiList.editVehicle, form);
    setSaving(false);
    
    if (data?.code === '200') {
      setSnack({ open: true, msg: 'Vehicle updated successfully', sev: 'success' });
      // Refresh vehicle data including image
      await loadVehicleData();
      setActiveTab('Vehicle');
    } else {
      setSnack({ open: true, msg: data?.message || 'Failed to update', sev: 'error' });
    }
  };

  const f = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  // Document handlers
  const handleAddDocument = async () => {
    if (!docForm.title.trim()) {
      setSnack({ open: true, msg: 'Please enter a document title', sev: 'error' });
      return;
    }
    if (!docForm.file) {
      setSnack({ open: true, msg: 'Please select a file', sev: 'error' });
      return;
    }

    setSavingDoc(true);
    try {
      const formData = new FormData();
      formData.append('vehicle_id', vehicleId);
      formData.append('title', docForm.title.trim());
      formData.append('document', docForm.file);
      formData.append('document_type', docForm.document_type);
      formData.append('notes', docForm.notes.trim());
      formData.append('expiry_date', docForm.expiry_date);
      
      const data = await postImg(Config.base_url + '/admin/vehicle/add-document', formData);
      if (data?.code === '200') {
        setDocDialog(false);
        setDocForm({ title: '', file: null, document_type: 'other', notes: '', expiry_date: '' });
        setDocFileName('');
        // Refresh documents
        const refresh = await InvokeApi(Config.base_url + '/admin/vehicle/documents', { vehicle_id: vehicleId });
        setDocuments(refresh?.documents || []);
        setSnack({ open: true, msg: 'Document uploaded successfully', sev: 'success' });
      } else {
        setSnack({ open: true, msg: data?.message || 'Upload failed', sev: 'error' });
      }
    } catch (err) {
      setSnack({ open: true, msg: 'Upload failed: ' + err.message, sev: 'error' });
    }
    setSavingDoc(false);
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    const data = await InvokeApi(Config.base_url + '/admin/vehicle/delete-document', { id: docId });
    if (data?.code === '200') {
      setDocuments(prev => prev.filter(d => d.id !== docId));
      setSnack({ open: true, msg: 'Document deleted', sev: 'success' });
    } else {
      setSnack({ open: true, msg: data?.message || 'Delete failed', sev: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  if (!vehicle) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="text.secondary">Vehicle not found</Typography>
        <Button onClick={() => navigate('/vehicles')} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  const batteryColor = vehicle.battery_pct >= 60 ? '#4CAF50' : vehicle.battery_pct >= 30 ? '#FF9800' : '#F44336';
  const statusColor = { active: '#4CAF50', assigned: '#2196F3', maintenance: '#FF9800', retired: '#F44336' };

  const getExpiryAlert = (expiryDate) => {
    if (!expiryDate) return null;
    const days = Math.floor((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return { severity: 'error', message: 'EXPIRED' };
    if (days <= 30) return { severity: 'warning', message: `Expires in ${days} days` };
    return null;
  };

  const insuranceAlert = getExpiryAlert(vehicle.insurance_expiry);
  const rcAlert = getExpiryAlert(vehicle.rc_expiry);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const TABS = [
    { id: 'Vehicle', label: 'Vehicle Information' },
    { id: 'Documents', label: 'Documents' },
    { id: 'History', label: 'History' },
    { id: 'Edit', label: 'Edit' },
  ];

  return (
    <Box>
      {/* Header - Simple back button and title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Tooltip title="Back">
          <IconButton onClick={() => navigate('/vehicles')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack sx={{ color: '#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">{vehicle.vehicle_number}</Typography>
          <Typography variant="body2" color="text.secondary">
            ID: #{vehicle.id} · {vehicle.model} · {vehicle.color}
          </Typography>
        </Box>
      </Box>

      {/* Alerts */}
      {(insuranceAlert || rcAlert) && (
        <Box sx={{ mb: 2 }}>
          {insuranceAlert && <Alert severity={insuranceAlert.severity} sx={{ mb: 1 }}><strong>Insurance:</strong> {insuranceAlert.message}</Alert>}
          {rcAlert && <Alert severity={rcAlert.severity}><strong>RC Document:</strong> {rcAlert.message}</Alert>}
        </Box>
      )}

      <Grid container spacing={3}>
        
        {/* Left Sidebar (3 cols) - Sticky */}
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'sticky', top: 80 }}>
            {/* Header gradient */}
            <Box sx={{ height: 70, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }} />
            
            <CardContent sx={{ pt: 0, textAlign: 'center', pb: 3 }}>
              {/* Vehicle Image with upload button */}
              <Box sx={{ position: 'relative', display: 'inline-block', mt: -5 }}>
                <Avatar
                  src={vehicle.vehicle_image ? `${vehicle.vehicle_image}` : undefined}
                  sx={{
                    width: 80,
                    height: 80,
                    border: '3px solid #fff',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                    fontSize: 28,
                    fontWeight: 900,
                    color: '#fff',
                  }}
                >
                  {!vehicle.vehicle_image && (vehicle.vehicle_number?.substring(0, 2) || 'V')}
                </Avatar>
                
                <label htmlFor="vehicle-image-upload">
                  <input
                    id="vehicle-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                  <Tooltip title="Change Vehicle Photo">
                    <IconButton
                      component="span"
                      size="small"
                      disabled={uploadingImage}
                      sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: -2,
                        background: '#1a237e',
                        color: '#fff',
                        width: 26,
                        height: 26,
                        '&:hover': { background: '#1565c0' },
                      }}
                    >
                      {uploadingImage ? <CircularProgress size={12} color="inherit" /> : <PhotoCamera sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Tooltip>
                </label>
              </Box>

              <Typography fontWeight={800} fontSize={15} mt={1.5}>{vehicle.vehicle_number}</Typography>
              <Typography fontSize={12} color="text.secondary">{vehicle.model}</Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                <Chip label={`#${vehicle.id}`} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 10 }} />
                <Chip
                  label={vehicle.status?.toUpperCase() || 'ACTIVE'}
                  size="small"
                  sx={{
                    background: vehicle.status === 'active' ? '#e8f5e9' : vehicle.status === 'assigned' ? '#e3f2fd' : '#ffebee',
                    color: vehicle.status === 'active' ? '#2e7d32' : vehicle.status === 'assigned' ? '#0277bd' : '#c62828',
                    fontWeight: 600,
                    fontSize: 10,
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Quick Info Icons */}
              <Box sx={{ textAlign: 'left', px: 1 }}>
                {[
                  { icon: <DirectionsCar fontSize="small" />, value: `${vehicle.model} ${vehicle.year}` },
                  { icon: <Phone fontSize="small" />, value: vehicle.assigned_phone },
                  { icon: <LocationOn fontSize="small" />, value: vehicle.color },
                  { icon: <Speed fontSize="small" />, value: vehicle.odometer ? `${vehicle.odometer} km` : null },
                ].map((r, i) => r.value ? (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
                    <Box sx={{ color: '#1a237e', flexShrink: 0 }}>{r.icon}</Box>
                    <Typography fontSize={12} color="text.secondary" sx={{ wordBreak: 'break-all' }}>{r.value}</Typography>
                  </Box>
                ) : null)}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Battery Level */}
              <Box sx={{ mb: 2, p: 2, background: '#f8fafc', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight={700} fontSize={11}>Battery Level</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: batteryColor, fontSize: 11 }}>
                    {vehicle.battery_pct ?? '—'}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={parseInt(vehicle.battery_pct ?? 0)}
                  sx={{ height: 6, borderRadius: 1, bgcolor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': { borderRadius: 1, bgcolor: batteryColor } }}
                />
              </Box>

              {/* Assigned User Info */}
              {vehicle.assigned_user && vehicle.user_id && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box 
                    onClick={() => navigate(`/user/${vehicle.user_id}`)}
                    sx={{ 
                      p: 2, 
                      background: 'linear-gradient(135deg,#e3f2fd,#bbdefb)', 
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: 'linear-gradient(135deg,#bbdefb,#90caf9)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(33,150,243,0.3)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: '#1976d2', 
                          fontSize: 14, 
                          fontWeight: 700 
                        }}
                      >
                        {vehicle.assigned_user[0].toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#0277bd', fontSize: 9, textTransform: 'uppercase' }}>
                          ASSIGNED TO
                        </Typography>
                        <Typography variant="body2" fontWeight={800} color="#0277bd" fontSize={13}>
                          {vehicle.assigned_user}
                        </Typography>
                      </Box>
                      <ArrowBack sx={{ fontSize: 16, color: '#0277bd', transform: 'rotate(180deg)' }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Phone sx={{ fontSize: 12, color: '#0277bd' }} />
                      <Typography variant="caption" color="#0277bd" fontWeight={600}>
                        {vehicle.assigned_phone || '—'}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1, borderColor: 'rgba(2,119,189,0.2)' }} />
                    <Typography variant="caption" color="text.secondary" fontSize={10}>
                      Assigned: {formatDate(vehicle.assigned_date)}
                    </Typography>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Button
                fullWidth
                variant="contained"
                startIcon={<GpsFixed />}
                onClick={openTrackDialog}
                sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#2e7d32,#388e3c)', fontSize: 12, fontWeight: 600, mb: 1 }}
              >
                Track Vehicle
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Assessment />}
                onClick={openSummaryDialog}
                sx={{ borderRadius: 2, color: '#6a1b9a', borderColor: '#6a1b9a', fontSize: 12, fontWeight: 600, mb: 1, '&:hover': { background: '#f3e5f5' } }}
              >
                Monthly Summary
              </Button>
              {vehicle.device_imei && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Speed />}
                  onClick={() => setControlDialog(true)}
                  sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#c62828,#d32f2f)', fontSize: 12, fontWeight: 600, mb: 1, color: '#fff' }}
                >
                  Remote Control
                </Button>
              )}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setActiveTab('Edit')}
                sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontSize: 12, fontWeight: 600, mb: 1, '&:hover': { background: '#e8eaf6' } }}
              >
                Edit Vehicle
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Delete />}
                onClick={handleDelete}
                sx={{ borderRadius: 2, color: '#c62828', borderColor: '#c62828', fontSize: 12, fontWeight: 600, '&:hover': { background: '#ffebee' } }}
              >
                Delete Vehicle
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Content (9 cols) */}
        <Grid item xs={12} md={9}>
          
          {/* Pill-style Tabs */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {TABS.map((t) => (
              <Box
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 2,
                  py: 1,
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: activeTab === t.id ? '#1a237e' : '#e8eaf6',
                  background: activeTab === t.id ? '#e8eaf6' : '#fff',
                  transition: 'all 0.2s',
                }}
              >
                <Typography fontSize={13} fontWeight={700} color={activeTab === t.id ? '#1a237e' : 'text.secondary'}>
                  {t.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Tab Content */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              
              {/* Vehicle Information Tab */}
              {activeTab === 'Vehicle' && (
                <>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Vehicle Information</Typography>
                  <Grid container spacing={0}>
                    {[
                      { label: 'Vehicle Number', value: vehicle.vehicle_number },
                      { label: 'Vehicle Name', value: vehicle.vehicle_name },
                      { label: 'Chassis Number', value: vehicle.chassis_number },
                      { label: 'Model', value: vehicle.model },
                      { label: 'Color', value: vehicle.color },
                      { label: 'Year', value: vehicle.year },
                      { label: 'Status', value: vehicle.status?.toUpperCase() },
                      { label: 'Device IMEI', value: vehicle.device_imei },
                      { label: 'Battery Level', value: vehicle.battery_pct ? `${vehicle.battery_pct}%` : null },
                      { label: 'Battery Capacity', value: vehicle.battery_capacity ? `${vehicle.battery_capacity} kWh` : null },
                      { label: 'Battery Health', value: vehicle.battery_health_pct ? `${vehicle.battery_health_pct}%` : null },
                      { label: 'Range', value: vehicle.range_km ? `${vehicle.range_km} km` : null },
                      { label: 'Odometer', value: vehicle.odometer ? `${vehicle.odometer} km` : null },
                      { label: 'Charging Port Type', value: vehicle.charging_port_type ? (
                        vehicle.charging_port_type === 'type1' ? 'Type 1 (J1772)' :
                        vehicle.charging_port_type === 'type2' ? 'Type 2 (Mennekes)' :
                        vehicle.charging_port_type === 'ccs' ? 'CCS' :
                        vehicle.charging_port_type === 'chademo' ? 'CHAdeMO' :
                        vehicle.charging_port_type === 'tesla' ? 'Tesla' : vehicle.charging_port_type
                      ) : null },
                      { label: 'Max Charging Rate', value: vehicle.max_charging_rate_kw ? `${vehicle.max_charging_rate_kw} kW` : null },
                      { label: 'GPS Status', value: vehicle.is_online ? '🟢 ONLINE' : '🔴 OFFLINE' },
                      { label: 'Last GPS Update', value: vehicle.last_gps_update ? new Date(vehicle.last_gps_update).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : null },
                      { label: 'Insurance Expiry', value: vehicle.insurance_expiry ? formatDate(vehicle.insurance_expiry) : null },
                      { label: 'RC Expiry', value: vehicle.rc_expiry ? formatDate(vehicle.rc_expiry) : null },
                      { label: 'Assigned User', value: vehicle.assigned_user },
                      { label: 'Assigned By', value: vehicle.assigned_by },
                      { label: 'Assigned Date', value: vehicle.assigned_date ? formatDate(vehicle.assigned_date) : null },
                      { label: 'Assigned Phone', value: vehicle.assigned_phone },
                      { label: 'Notes', value: vehicle.notes },
                    ].map(({ label, value }) => (
                      <Grid item xs={12} sm={6} key={label} sx={{ px: 1 }}>
                        <InfoRow label={label} value={value} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {/* Documents Tab */}
              {activeTab === 'Documents' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Documents</Typography>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => setDocDialog(true)}
                      sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 12, fontWeight: 600 }}
                    >
                      Upload Document
                    </Button>
                  </Box>

                  {docsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : documents.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, background: '#f8fafc', borderRadius: 2 }}>
                      <Typography color="text.secondary" fontWeight={600}>No documents uploaded yet</Typography>
                      <Typography variant="caption" color="text.secondary">Click "Upload Document" to add documents</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {documents.map((doc) => (
                        <Grid item xs={12} sm={6} md={4} key={doc.id}>
                          <Card sx={{ p: 2, border: '1px solid #e8eaf6', borderRadius: 2, '&:hover': { borderColor: '#1a237e', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              <Box sx={{ width: 40, height: 40, borderRadius: 1, background: '#e8eaf6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Edit sx={{ color: '#1a237e', fontSize: 20 }} />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography fontSize={13} fontWeight={700} color="#1a237e" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                                  {doc.title}
                                </Typography>
                                <Typography fontSize={11} color="text.secondary" sx={{ mb: 0.3 }}>
                                  Type: <strong>{doc.document_type || 'other'}</strong>
                                </Typography>
                                {doc.expiry_date && (
                                  <Typography fontSize={11} color="text.secondary" sx={{ mb: 0.3 }}>
                                    Expires: <strong>{formatDate(doc.expiry_date)}</strong>
                                  </Typography>
                                )}
                                {doc.notes && (
                                  <Typography fontSize={10} color="text.secondary" sx={{ mb: 0.5, fontStyle: 'italic' }}>
                                    {doc.notes}
                                  </Typography>
                                )}
                                <Typography fontSize={10} color="text.secondary" sx={{ mb: 1 }}>
                                  Uploaded: {formatDate(doc.created_at)}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => window.open(`${Config.base_url}/${doc.file_path}`, '_blank')}
                                    sx={{ fontSize: 10, py: 0.5 }}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleDeleteDocument(doc.id)}
                                    sx={{ fontSize: 10, py: 0.5 }}
                                  >
                                    Delete
                                  </Button>
                                </Box>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}

              {/* History Tab */}
              {activeTab === 'History' && (
                <>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Vehicle Assignment History</Typography>
                  
                  {historyLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : history.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, background: '#f8fafc', borderRadius: 2 }}>
                      <Typography color="text.secondary" fontWeight={600}>No assignment history found</Typography>
                      <Typography variant="caption" color="text.secondary" mt={1}>Assignments and returns will appear here</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      {/* Timeline line */}
                      <Box sx={{ position: 'absolute', left: 19, top: 0, bottom: 0, width: 2, background: '#e8eaf6' }} />
                      
                      {history.map((record, i) => {
                        const isAssigned = record.event_type === 'assigned';
                        const dotColor = isAssigned ? '#16a34a' : '#f59e0b';
                        const bgColor = isAssigned ? '#f0fdf4' : '#fffbeb';
                        
                        return (
                          <Box key={i} sx={{ display: 'flex', gap: 2, mb: 3, position: 'relative' }}>
                            {/* Timeline dot */}
                            <Box sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              background: bgColor, 
                              border: `3px solid ${dotColor}`, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              flexShrink: 0, 
                              zIndex: 1 
                            }}>
                              {isAssigned ? (
                                <Assignment sx={{ fontSize: 18, color: dotColor }} />
                              ) : (
                                <AssignmentReturn sx={{ fontSize: 18, color: dotColor }} />
                              )}
                            </Box>
                            
                            {/* Content */}
                            <Card sx={{ flex: 1, p: 2.5, border: `1px solid ${dotColor}40`, borderRadius: 2, background: bgColor }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Typography fontSize={14} fontWeight={700} color={dotColor}>
                                  {isAssigned ? 'Vehicle Assigned' : 'Vehicle Returned'}
                                </Typography>
                                <Chip
                                  label={`${formatDate(record.date)} ${record.time ? `at ${record.time}` : ''}`}
                                  size="small"
                                  sx={{ background: '#fff', color: dotColor, fontSize: 10, fontWeight: 600, height: 22, border: `1px solid ${dotColor}40` }}
                                />
                              </Box>
                              
                              <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                  <Typography fontSize={10} color="text.secondary" fontWeight={600}>Rider Name</Typography>
                                  <Typography fontSize={13} fontWeight={600}>{record.user_name || '—'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography fontSize={10} color="text.secondary" fontWeight={600}>Phone</Typography>
                                  <Typography fontSize={13} fontWeight={600} sx={{ fontFamily: 'monospace' }}>{record.user_phone || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography fontSize={10} color="text.secondary" fontWeight={600}>
                                    {isAssigned ? 'Assigned By' : 'Collected By'}
                                  </Typography>
                                  <Typography fontSize={13} fontWeight={600}>{record.by_name || '—'}</Typography>
                                </Grid>
                                {!isAssigned && record.notes && (
                                  <Grid item xs={12}>
                                    <Typography fontSize={10} color="text.secondary" fontWeight={600}>Return Notes</Typography>
                                    <Typography fontSize={12} sx={{ mt: 0.5, p: 1, background: '#fff', borderRadius: 1, border: '1px solid #f59e0b40' }}>
                                      {record.notes}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}

              {/* Edit Tab */}
              {activeTab === 'Edit' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Edit Vehicle</Typography>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={handleSave}
                      disabled={saving}
                      sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 12, fontWeight: 600 }}
                    >
                      {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
                    </Button>
                  </Box>

                  <Grid container spacing={2.5}>
                    {/* Vehicle Identity */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8, mb: 1 }}>
                        Vehicle Identity
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Vehicle Number *" value={form.vehicle_number} onChange={f('vehicle_number')} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Chassis Number *" value={form.chassis_number} onChange={f('chassis_number')} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Vehicle Name" placeholder="e.g., Blue Scooter 1" value={form.vehicle_name || ''} onChange={f('vehicle_name')} helperText="Friendly name for easy identification" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Model *" value={form.model} onChange={f('model')} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Color" value={form.color} onChange={f('color')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Year" type="number" value={form.year} onChange={f('year')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" select label="Status" value={form.status} onChange={f('status')}>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="assigned">Assigned</MenuItem>
                        <MenuItem value="maintenance">Maintenance</MenuItem>
                        <MenuItem value="retired">Retired</MenuItem>
                      </TextField>
                    </Grid>

                    {/* Battery & Performance */}
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8, mb: 1 }}>
                        Battery & Performance
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Battery Level (%)" type="number" value={form.battery_pct} onChange={f('battery_pct')} inputProps={{ min: 0, max: 100 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Battery Capacity (kWh)" type="number" value={form.battery_capacity} onChange={f('battery_capacity')} helperText="Total battery capacity" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Battery Health (%)" type="number" value={form.battery_health_pct || ''} onChange={f('battery_health_pct')} inputProps={{ min: 0, max: 100 }} helperText="Battery condition/degradation" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Range (km)" type="number" value={form.range_km} onChange={f('range_km')} helperText="Max range on full charge" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Odometer (km)" type="number" value={form.odometer} onChange={f('odometer')} />
                    </Grid>

                    {/* Charging Specifications */}
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8, mb: 1 }}>
                        Charging Specifications
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" select label="Charging Port Type" value={form.charging_port_type || ''} onChange={f('charging_port_type')}>
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="type1">Type 1 (J1772)</MenuItem>
                        <MenuItem value="type2">Type 2 (Mennekes)</MenuItem>
                        <MenuItem value="ccs">CCS (Combined Charging System)</MenuItem>
                        <MenuItem value="chademo">CHAdeMO</MenuItem>
                        <MenuItem value="tesla">Tesla Proprietary</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Max Charging Rate (kW)" type="number" value={form.max_charging_rate_kw || ''} onChange={f('max_charging_rate_kw')} helperText="Maximum charging power" />
                    </Grid>

                    {/* Device Status (Read-only) */}
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8, mb: 1 }}>
                        Device Status
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, background: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: vehicle.is_online ? '#16a34a' : '#dc2626' }} />
                          <Typography fontSize={11} color="text.secondary" fontWeight={600}>GPS Device Status</Typography>
                        </Box>
                        <Typography fontSize={14} fontWeight={700} color={vehicle.is_online ? '#16a34a' : '#dc2626'}>
                          {vehicle.is_online ? 'ONLINE' : 'OFFLINE'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, background: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <Typography fontSize={11} color="text.secondary" fontWeight={600} mb={0.5}>Last GPS Update</Typography>
                        <Typography fontSize={14} fontWeight={700}>
                          {vehicle.last_gps_update ? new Date(vehicle.last_gps_update).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'Never'}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Documents & Device */}
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8, mb: 1 }}>
                        Documents & GPS Device
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Insurance Expiry" type="date" value={form.insurance_expiry} onChange={f('insurance_expiry')} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="RC Expiry" type="date" value={form.rc_expiry} onChange={f('rc_expiry')} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Device IMEI (15 digits)" value={form.device_imei} onChange={f('device_imei')} helperText="GPS tracker IMEI number" />
                    </Grid>

                    {/* Notes */}
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Admin Notes" value={form.notes} onChange={f('notes')} multiline rows={3} placeholder="Add any notes about this vehicle..." />
                    </Grid>
                  </Grid>
                </>
              )}

            </CardContent>
          </Card>

        </Grid>

      </Grid>

      {/* ── Track Vehicle Dialog (Live GPS — Google Maps embed, auto-refresh 10s) ── */}
      <Dialog open={trackDialog} onClose={closeTrackDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn sx={{ color: '#2e7d32' }} />
            Track — {vehicle?.vehicle_number}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {trackLoading && !trackLocation ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : trackLocation?.latitude ? (
            <Box>
              {/* GPS info card */}
              <Box sx={{ p: 2, background: '#e8f5e9', borderRadius: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GpsFixed sx={{ color: '#2e7d32' }} />
                  <Typography fontWeight={700} color="#2e7d32">GPS Active</Typography>
                  {trackLoading && <CircularProgress size={14} sx={{ color: '#2e7d32', ml: 1 }} />}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box>
                    <Typography fontSize={11} color="text.secondary">Latitude</Typography>
                    <Typography fontSize={13} fontWeight={600}>{parseFloat(trackLocation.latitude).toFixed(6)}</Typography>
                  </Box>
                  <Box>
                    <Typography fontSize={11} color="text.secondary">Longitude</Typography>
                    <Typography fontSize={13} fontWeight={600}>{parseFloat(trackLocation.longitude).toFixed(6)}</Typography>
                  </Box>
                  {trackLocation.speed != null && (
                    <Box>
                      <Typography fontSize={11} color="text.secondary">Speed</Typography>
                      <Typography fontSize={13} fontWeight={600}>{parseFloat(trackLocation.speed || 0).toFixed(1)} km/h</Typography>
                    </Box>
                  )}
                  {(trackLocation.received_at || trackLocation.timestamp) && (
                    <Box>
                      <Typography fontSize={11} color="text.secondary">Last Update</Typography>
                      <Typography fontSize={13} fontWeight={600}>
                        {dayjs(String(trackLocation.received_at || trackLocation.timestamp).replace(' ', 'T')).format('hh:mm:ss A')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              {/* Google Maps embed */}
              <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2, border: '1px solid #e8eaf6' }}>
                <iframe
                  title="vehicle-location"
                  width="100%" height="260"
                  style={{ border: 'none', display: 'block' }}
                  src={`https://maps.google.com/maps?q=${trackLocation.latitude},${trackLocation.longitude}&z=15&output=embed`}
                />
              </Box>
              <Button fullWidth variant="contained" startIcon={<LocationOn />}
                href={`https://www.google.com/maps?q=${trackLocation.latitude},${trackLocation.longitude}`}
                target="_blank"
                sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 2, fontWeight: 700, mb: 1 }}>
                Open in Google Maps
              </Button>
              <Typography fontSize={11} color="text.secondary" textAlign="center">Auto-refreshes every 10 seconds</Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <GpsOff sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
              <Typography color="text.secondary" fontWeight={500}>No GPS data available</Typography>
              <Typography variant="caption" color="text.secondary">GPS device may be offline or not transmitting</Typography>
              <Box mt={2}>
                <Button variant="outlined" onClick={() => fetchLiveLocation(vehicleId)}
                  sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Retry</Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={closeTrackDialog} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Monthly Summary Dialog ── */}
      <Dialog open={summaryDialog} onClose={() => setSummaryDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#6a1b9a', borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment sx={{ color: '#6a1b9a' }} />
              <Box>
                <Typography fontWeight={700} color="#6a1b9a">Monthly Summary — {vehicle?.vehicle_number}</Typography>
                <Typography fontSize={12} color="text.secondary">{vehicle?.model}</Typography>
              </Box>
            </Box>
            <TextField
              type="month"
              size="small"
              value={summaryMonth}
              onChange={e => { setSummaryMonth(e.target.value); fetchSummary(e.target.value); }}
              inputProps={{ max: dayjs().format('YYYY-MM') }}
              sx={{ minWidth: 160 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {summaryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#6a1b9a' }} /></Box>
          ) : !summaryData ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Assessment sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
              <Typography color="text.secondary" fontWeight={500}>{summaryError || 'No data available'}</Typography>
              {summaryError && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{summaryError}</Typography>}
              <Button variant="outlined" size="small" sx={{ mt: 2, borderRadius: 2, color: '#6a1b9a', borderColor: '#6a1b9a' }}
                onClick={() => fetchSummary(summaryMonth)}>Retry</Button>
            </Box>
          ) : !summaryData.daily_breakdown?.length ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Timeline sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
              <Typography color="text.secondary" fontWeight={500}>
                No travel data for {summaryData.period?.month_name} {summaryData.period?.year}
              </Typography>
              <Typography variant="caption" color="text.secondary">GPS data will appear once the vehicle starts transmitting</Typography>
            </Box>
          ) : (
            <Box>
              {/* Total KM banner */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 1, background: 'linear-gradient(135deg,#f3e5f5,#e8eaf6)', mb: 3, textAlign: 'center', border: '1px solid #ce93d8' }}>
                <Typography fontSize={12} fontWeight={700} color="#6a1b9a" textTransform="uppercase" letterSpacing={1} mb={0.5}>
                  {summaryData.period?.month_name} {summaryData.period?.year} — Total Distance
                </Typography>
                <Typography fontSize={52} fontWeight={900} color="#6a1b9a" lineHeight={1}>
                  {summaryData.total_km}
                </Typography>
                <Typography fontSize={16} fontWeight={700} color="#6a1b9a">km</Typography>
                <Chip label={`${summaryData.active_days} active days`} size="small"
                  sx={{ mt: 1, background: '#ce93d8', color: '#fff', fontWeight: 700, fontSize: 11 }} />
              </Paper>

              {/* Daily distance table */}
              <Typography fontWeight={700} fontSize={13} color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Daily Distance
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#f5f6fa' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6a1b9a' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6a1b9a' }}>Distance</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6a1b9a' }}>Track Path</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryData.daily_breakdown.map((day, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                          {dayjs(day.date).format('DD MMM YYYY')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${day.distance_km} km`}
                            size="small"
                            sx={{ background: day.distance_km > 0 ? '#e8eaf6' : '#f5f5f5', color: day.distance_km > 0 ? '#1a237e' : '#999', fontWeight: 700, fontSize: 12 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View GPS path on map">
                            <Button size="small" variant="contained" startIcon={<MapIcon sx={{ fontSize: 13 }} />}
                              onClick={() => openPathDialog(day)}
                              sx={{ background: 'linear-gradient(135deg,#0277bd,#0288d1)', borderRadius: 1.5, fontWeight: 700, fontSize: 10, px: 1, minWidth: 0 }}>
                              Track Path
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setSummaryDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Track Path Dialog (daily GPS — Leaflet map) ── */}
      <Dialog open={pathDialog} onClose={closePathDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0277bd', borderBottom: '1px solid #f0f0f0', pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={closePathDialog} sx={{ mr: 0.5 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <MapIcon sx={{ color: '#0277bd' }} />
            <Box>
              <Typography fontWeight={700} color="#0277bd">GPS Track Path — {vehicle?.vehicle_number}</Typography>
              <Typography fontSize={12} color="text.secondary">
                {pathDate ? dayjs(pathDate).format('dddd, DD MMMM YYYY') : ''}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {pathLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
              <CircularProgress sx={{ color: '#0277bd' }} />
              <Typography color="text.secondary" fontSize={13}>Loading GPS path data…</Typography>
            </Box>
          ) : pathError ? (
            <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
              <LocationOn sx={{ fontSize: 56, color: '#e8eaf6', mb: 1.5 }} />
              <Typography color="text.secondary" fontWeight={600} fontSize={15}>No path data available</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{pathError}</Typography>
            </Box>
          ) : pathPoints.length > 0 ? (
            <Box>
              {/* Stats + layer switcher bar */}
              <Box sx={{ display: 'flex', gap: 2, px: 2.5, py: 1.5, background: '#e3f2fd', borderBottom: '1px solid #bbdefb', flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#2e7d32', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  <Typography fontSize={12} fontWeight={600} color="#2e7d32">
                    Start: {dayjs(String(pathPoints[0]?.timestamp).replace(' ', 'T')).format('hh:mm:ss A')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#c62828', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  <Typography fontSize={12} fontWeight={600} color="#c62828">
                    End: {dayjs(String(pathPoints[pathPoints.length - 1]?.timestamp).replace(' ', 'T')).format('hh:mm:ss A')}
                  </Typography>
                </Box>
                {/* <Chip label={`${pathPoints.length} GPS points`} size="small"
                  sx={{ background: '#bbdefb', color: '#0277bd', fontWeight: 700, fontSize: 11 }} /> */}
                {/* Map layer switcher */}
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                  {Object.entries(MAP_LAYERS).map(([key, val]) => (
                    <Button key={key} size="small"
                      variant={pathMapLayer === key ? 'contained' : 'outlined'}
                      onClick={() => setPathMapLayer(key)}
                      sx={{ fontSize: 10, fontWeight: 700, px: 1, py: 0.3, minWidth: 0, borderRadius: 1.5,
                        ...(pathMapLayer === key
                          ? { background: '#0277bd', color: '#fff', borderColor: '#0277bd' }
                          : { color: '#0277bd', borderColor: '#90caf9', background: '#fff' }) }}>
                      {val.label}
                    </Button>
                  ))}
                </Box>
              </Box>
              {/* Leaflet map container */}
              <Box ref={pathMapRef} sx={{ width: '100%', height: 460 }} />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={closePathDialog} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.sev} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* Document Upload Dialog */}
      <Dialog open={docDialog} onClose={() => setDocDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadFile />
            <span>Upload Document</span>
          </Box>
          <IconButton size="small" onClick={() => setDocDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Document Title *"
                value={docForm.title}
                onChange={(e) => setDocForm(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                select
                label="Document Type"
                value={docForm.document_type}
                onChange={(e) => setDocForm(prev => ({ ...prev, document_type: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="registration">Registration (RC)</option>
                <option value="insurance">Insurance</option>
                <option value="permit">Permit</option>
                <option value="fitness">Fitness Certificate</option>
                <option value="puc">PUC Certificate</option>
                <option value="other">Other</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Expiry Date (Optional)"
                value={docForm.expiry_date}
                onChange={(e) => setDocForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Notes (Optional)"
                value={docForm.notes}
                onChange={(e) => setDocForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any additional information about this document"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadFile />}
                sx={{ borderRadius: 2, textTransform: 'none', py: 1.5 }}
              >
                {docFileName || 'Choose File'}
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setDocForm(prev => ({ ...prev, file }));
                      setDocFileName(file.name);
                    }
                  }}
                />
              </Button>
              {docFileName && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Selected: {docFileName}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDocDialog(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddDocument}
            disabled={savingDoc}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700 }}
          >
            {savingDoc ? <CircularProgress size={18} color="inherit" /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remote Control Dialog */}
      <Dialog open={controlDialog} onClose={() => setControlDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#c62828', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg,#ffebee,#ffcdd2)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Speed sx={{ color: '#c62828' }} />
            <span style={{ color: '#c62828' }}>Remote Vehicle Control</span>
          </Box>
          <IconButton size="small" onClick={() => setControlDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography fontWeight={700} fontSize={13} mb={0.5}>
              ⚠️ Caution: Remote Control Feature
            </Typography>
            <Typography fontSize={12}>
              This feature sends commands directly to the vehicle's TFT100 tracker. 
              <strong> Use responsibly.</strong> Immobilizing a vehicle in motion can be dangerous.
            </Typography>
          </Alert>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight={700} color="text.secondary" mb={1}>
                Vehicle: {vehicle.vehicle_number}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tracker IMEI: {vehicle.device_imei}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Command *"
                value={controlCommand}
                onChange={(e) => setControlCommand(e.target.value)}
                required
              >
                <MenuItem value="">-- Select Command --</MenuItem>
                <MenuItem value="immobilize">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#c62828' }} />
                    <span>Immobilize (Stop Vehicle)</span>
                  </Box>
                </MenuItem>
                <MenuItem value="enable">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                    <span>Enable (Allow Vehicle)</span>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason (Optional)"
                placeholder="Enter reason for this action..."
                value={controlReason}
                onChange={(e) => setControlReason(e.target.value)}
              />
            </Grid>

            {controlCommand === 'immobilize' && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ fontSize: 12 }}>
                  <strong>Immobilize:</strong> This will disable the vehicle's engine. The vehicle will stop running and cannot be started until enabled again.
                </Alert>
              </Grid>
            )}

            {controlCommand === 'enable' && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ fontSize: 12 }}>
                  <strong>Enable:</strong> This will restore normal vehicle operation. The engine can be started.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ background: '#f8fafc', p: 2, borderRadius: 1 }}>
                <strong>How it works:</strong><br />
                1. Command is queued on the server<br />
                2. Tracker fetches pending commands on next connection (usually within 30 seconds)<br />
                3. Tracker executes the command<br />
                4. Status is updated automatically
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setControlDialog(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRemoteControl}
            disabled={controlLoading || !controlCommand}
            sx={{ 
              background: controlCommand === 'immobilize' 
                ? 'linear-gradient(135deg,#c62828,#d32f2f)' 
                : 'linear-gradient(135deg,#2e7d32,#388e3c)', 
              fontWeight: 700 
            }}
          >
            {controlLoading ? <CircularProgress size={18} color="inherit" /> : 'Send Command'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default VehicleDetail;
